// @djsem/analysis-contract — Event & Release Value-Add Layer
// Prompt 43: Event Brief Builder
// Prompt 45: Technical Rider / Venue Handoff
// Prompt 46: Missing Track Finder / Playlist Gap Analysis
// Prompt 47: EPK / Press Kit Generator
// Prompt 52: Brand Consistency Scoring
// Prompt 53: Release Readiness Coach

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult } from "@djsem/shared";
import { SeqCounter } from "@djsem/shared";
const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// ============================================================================
// PROMPT 45 — Technical Rider / Venue Handoff
// ============================================================================

/** A named contact (venue tech, coordinator, etc.) attached to a technical rider. */
export interface DJSEMTechnicalRiderContact {
  role: string; name: string; email?: string; phone?: string;
}

/** A single pre-show operator checklist task within a technical rider. */
export interface DJSEMOperatorChecklistItem {
  id: string;
  label: string;
  category: "setup" | "visuals" | "audio" | "lighting" | "safety" | "fallback" | "shutdown";
  required: boolean;
  status: "todo" | "done" | "not_applicable";
}

/** Standard non-negotiable safety disclaimers and priorities attached to every technical rider. */
export interface DJSEMVenueSafetyNotes {
  softwareOutputDisclaimer: string;
  laserSafetyDisclaimer: string;
  lightingSafetyDisclaimer: string;
  blackoutPriority: boolean;
  safeModePriority: boolean;
}

/** A venue technical handoff document: contacts, output requirements, operator checklist, and safety notes. */
export interface DJSEMTechnicalRider {
  id: string;
  projectId: string;
  eventBriefId?: string;
  gigPackId?: string;
  title: string;
  status: "draft" | "ready" | "exported" | "archived";
  artistName?: string;
  setDurationMinutes?: number;
  venueName?: string;
  contacts: DJSEMTechnicalRiderContact[];
  visualsRequired: boolean;
  outputFormats: string[];
  internetRequired: boolean;
  offlineFallbackRequired: boolean;
  operatorChecklist: DJSEMOperatorChecklistItem[];
  safetyNotes: DJSEMVenueSafetyNotes;
  fileManifestAssetIds: string[];
  warnings: string[];
  createdAt: string;
  updatedAt: string;
}

/** Input required to create a new {@link DJSEMTechnicalRider}. */
export interface CreateTechnicalRiderInput {
  projectId: string; title: string;
  eventBriefId?: string; gigPackId?: string;
  artistName?: string; setDurationMinutes?: number;
  venueName?: string; visualsRequired?: boolean;
}

const SAFETY_NOTES: DJSEMVenueSafetyNotes = {
  softwareOutputDisclaimer: "DJSEM provides creative show data and software output guidance. It is not certified hardware safety control.",
  laserSafetyDisclaimer: "Laser output requires external safety certification. DJSEM does not certify laser safety.",
  lightingSafetyDisclaimer: "Lighting control must be verified by qualified venue personnel.",
  blackoutPriority: true,
  safeModePriority: true,
};

/** Creates, validates, and readies technical riders, seeding a standard operator checklist and safety notes. */
export class TechnicalRiderService {
  /**
   * Creates a new draft technical rider with a standard operator checklist and the fixed safety notes.
   * @param input - the rider's project, title, venue, artist, and duration details.
   * @returns the newly created draft rider.
   */
  createRider(input: CreateTechnicalRiderInput): DJSEMTechnicalRider {
    const checklist: DJSEMOperatorChecklistItem[] = [
      { id: `oc_${_seq.next()}`, label: "Verify audio output", category: "setup", required: true, status: "todo" },
      { id: `oc_${_seq.next()}`, label: "Test visual output", category: "visuals", required: input.visualsRequired ?? false, status: input.visualsRequired ? "todo" : "not_applicable" },
      { id: `oc_${_seq.next()}`, label: "Confirm blackout procedure", category: "safety", required: true, status: "todo" },
      { id: `oc_${_seq.next()}`, label: "Confirm safe mode procedure", category: "safety", required: true, status: "todo" },
      { id: `oc_${_seq.next()}`, label: "Test fallback assets", category: "fallback", required: true, status: "todo" },
    ];
    return {
      id: `tr_${_seq.next()}`, projectId: input.projectId, title: input.title,
      eventBriefId: input.eventBriefId, gigPackId: input.gigPackId,
      status: "draft", artistName: input.artistName,
      setDurationMinutes: input.setDurationMinutes, venueName: input.venueName,
      contacts: [], visualsRequired: input.visualsRequired ?? false,
      outputFormats: [], internetRequired: false, offlineFallbackRequired: true,
      operatorChecklist: checklist, safetyNotes: SAFETY_NOTES,
      fileManifestAssetIds: [], warnings: [],
      createdAt: AT, updatedAt: AT,
    };
  }

  /**
   * Validates that a rider has the minimum required fields (title, venue name, set duration).
   * @param rider - the rider to validate.
   * @returns the validation result.
   */
  validateRider(rider: DJSEMTechnicalRider): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!rider.title.trim()) issues.push({ path: "$.title", message: "required" });
    if (!rider.venueName) issues.push({ path: "$.venueName", message: "venue name missing" });
    if (!rider.setDurationMinutes) issues.push({ path: "$.setDurationMinutes", message: "duration missing" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "technical_rider");
  }

  /**
   * Marks a rider ready if all required operator checklist items are complete.
   * @param rider - the rider to mark ready.
   * @returns a copy with `status: "ready"`, or the rider with an added warning if required checklist items are still pending.
   */
  markReady(rider: DJSEMTechnicalRider): DJSEMTechnicalRider {
    const blocking = rider.operatorChecklist.filter(c => c.required && c.status === "todo");
    if (blocking.length > 0) return { ...rider, warnings: [...rider.warnings, `${blocking.length} required checklist items pending`] };
    return { ...rider, status: "ready", updatedAt: AT };
  }
}
